// -----------------------------------------------------------------------------
// engine/export/export-path.ts — choose how to export.
//
// Reads the RuntimeConfig (derived from browser capabilities) plus the project to
// produce a concrete ExportPlan: container, codec, dimensions, fps, bitrate and a
// file name. This is the one place that decides "MP4/H.264 vs WebM/VP9" — the
// exporter and encode worker just follow the plan. (CLAUDE.md §7.)
// -----------------------------------------------------------------------------
import type { Project } from '../../core/model';
import type { RuntimeConfig } from '../../preflight/capabilities';
import type { ExportContainer, ExportVideoCodec } from '../../shared/worker-protocol';

export interface ExportPlan {
  container: ExportContainer;
  videoCodec: ExportVideoCodec;
  width: number;
  height: number;
  fps: number;
  bitrate: number;
  fileName: string;
  mimeType: string;
}

export function chooseExportPath(project: Project, config: RuntimeConfig): ExportPlan {
  // H.264/VP9 want even dimensions.
  const width = makeEven(project.width);
  const height = makeEven(project.height);
  const fps = Math.max(1, Math.round(project.fps));
  const bitrate = estimateBitrate(width, height, fps);
  const container = config.exportContainer;
  const ext = container === 'mp4' ? 'mp4' : 'webm';

  return {
    container,
    videoCodec: config.exportVideoCodec,
    width,
    height,
    fps,
    bitrate,
    fileName: `${sanitizeFileName(project.name) || 'zeroframe-export'}.${ext}`,
    mimeType: container === 'mp4' ? 'video/mp4' : 'video/webm',
  };
}

function makeEven(n: number): number {
  const v = Math.max(2, Math.round(n));
  return v % 2 === 0 ? v : v - 1;
}

// A simple bits-per-pixel heuristic, clamped to a sane range.
function estimateBitrate(width: number, height: number, fps: number): number {
  const raw = width * height * fps * 0.08;
  return Math.round(Math.min(40_000_000, Math.max(1_500_000, raw)));
}

function sanitizeFileName(name: string): string {
  return name
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '-')
    .slice(0, 80);
}
